import React from 'react'
import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import { T, useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import {
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { CustomerInfo } from '../../../common/types'
import { STEPS } from '../types'
import { SelectedCustomer } from '../../../common/SelectedCustomer'
import { CustomerSearch } from '../../../../../customer/customer-search/CustomerSearch'

interface Props {
  onStepChange: (step: STEPS) => void
  onClose: () => void
  onAdd: () => void
  onSelect: (customer: ApiCustomer | null) => void
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  group?: Group
}

interface Group {
  id: string
  name: string
}

export const [TEXT_DELIVERY, TEXT_PICKUP] = ['delivery', 'pickup']

const shouldDisableNext = (customer: ApiCustomer | null) => {
  return (
    Boolean(!customer) || !hasContactInfo(customer) || isInvalidEmail(customer)
  )
}

export const Step2Legacy: React.FC<Props> = ({
  onStepChange,
  onClose,
  onAdd,
  onSelect,
  onContactChange,
  newCustomer,
  selectedCustomer,
  group,
}) => {
  const t = useT()
  return (
    <Modal size="small" onClose={onClose}>
      <StepBreadcrumbs step={STEPS.CUSTOMER} />
      <ModalHeader>
        <T
          _str="Add customer for the fulfillment"
          _context="Mark as unfulfilled modal header"
        />
        <Text tag="p" className="vd-mt2">
          <T
            _str="Add a customer to record their contact information for the fulfillment."
            _context="Mark as unfulfilled modal header description"
          />
        </Text>
      </ModalHeader>
      <ModalContent>
        <>
          <h4 className="vd-text-label vd-mb2 vd-pl1">
            <T
              _str="Customer"
              _context="Mark as unfulfilled modal - customer label"
            />
          </h4>
          {selectedCustomer ? (
            <SelectedCustomer
              selectedCustomer={selectedCustomer}
              group={group}
              onContactChange={onContactChange}
              onSelect={onSelect}
            />
          ) : (
            <div className="vd-pl1">
              <CustomerSearch
                placeholder={t('Select or add a customer', {
                  _context:
                    'Placeholder for customer search in unfulfilled modal',
                })}
                onAdd={onAdd}
                onSelect={onSelect}
                searchValue={
                  (newCustomer && newCustomer.firstName) || undefined
                }
                suggestionsClassName="mark-as-unfulfilled-customer-popover"
              />
            </div>
          )}
        </>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            onClick={() => onStepChange(STEPS.METHOD)}
            variant="supplementary"
            data-track="back-method-step"
          >
            <T _str="Back" _context="Mark as unfulfilled modal - back button" />
          </Button>
          <Button
            onClick={() => onStepChange(STEPS.DETAILS)}
            disabled={shouldDisableNext(selectedCustomer)}
            variant="go"
            data-track="next-details-step"
          >
            <T _str="Next" _context="Mark as unfulfilled modal - next button" />
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step2Legacy.displayName = 'Step2Legacy'
